import ENV from "@/utils/env";
import { useEffect } from "react";
import { useGetAllSceneInsights } from "@/api/studio/insights/queries";
import { Card } from "@/components/ui/card";
import { NavButton } from "@/components/ui/navigation";
import { ReportsLayout } from "@/components/layouts";
import { SceneRender } from "@/features/reports/components/in-depth/scene-render";
import { ReportInDepthSkeleton } from "@/features/reports/components/in-depth/report-in-depth-skeleton";
import { useSegment } from "@/hooks/use-segment";
import { useFeatureFlag } from "@/hooks/use-feature-flag";
import { useGlobalStore } from "@/stores/global-store";
import { SceneMetricsDetails } from "@/features/reports/components/in-depth/scene-metrics-details";
import { useReportsSceneStore } from "@/features/reports/stores/use-reports-scene-store";
import { CopilotButton } from "@/features/reports/components/co-pilot-button";
import { Copilot } from "@/features/reports/components/copilot";
import { useLocation } from "wouter";

/* Prop Types */
interface ReportsSceneInDepthRouteProps {
  projectId: string;
  studyId: string;
  sceneKey: string;
}

/*
 * ReportsSceneInDepthRoute component
 *
 * This provides the route to deep dive into a specific scene within a study and view
 * different metrics and visualisation options.
 */
export const ReportsSceneInDepthRoute = ({ projectId, studyId, sceneKey }: ReportsSceneInDepthRouteProps) => {
  /* Queries */
  const { data: allScenes, isLoading: isAllScenesLoading } = useGetAllSceneInsights({ projectId, studyId });

  /* Store */
  const theme = useGlobalStore((state) => state.theme);
  const showCopilot = useReportsSceneStore((state) => state.showCopilot);
  const setShowCopilot = useReportsSceneStore((state) => state.setShowCopilot);

  /* Hooks */
  const { segmentPageEvent } = useSegment();
  const isSceneCopilotEnabled = useFeatureFlag("sceneCopilot");
  const [, setLocation] = useLocation();

  // Find the current scene based on the sceneKey prop
  const currentScene = allScenes?.find((scene) => scene.key === sceneKey);

  /* Handlers */
  const handleSceneClick = (sceneKey: string) => {
    // Navigate to the selected scene
    setLocation(`/projects/${projectId}/studies/${studyId}/scene/reports/in-depth/${sceneKey}`);
  };

  useEffect(() => {
    // segment page event when a user visits the scene report in depth page
    if (theme)
      segmentPageEvent("Scene Report", {
        title: "Scene Report | Dragonfly AI Studio",
        theme: theme,
      });
  }, [theme]);

  /* Render */
  return (
    <ReportsLayout projectId={projectId} studyId={studyId} hideSidebar>
      {/* Skeleton Loader */}
      {isAllScenesLoading && <ReportInDepthSkeleton uploadLength={3} />}

      {allScenes && !isAllScenesLoading && currentScene && (
        <>
          {/* Header */}
          <div className="flex flex-row items-center justify-end gap-2">
            <div className="flex flex-row items-center mr-auto">
              <NavButton navigationUrl={`/projects/${projectId}/studies/${studyId}/scene/reports/overview`} />
              <h2 className="font-semibold ml-2">{currentScene?.assetName}</h2>
            </div>
            {isSceneCopilotEnabled && <CopilotButton showChevron onClick={() => setShowCopilot(!showCopilot)} />}
          </div>

          {/* Thumbnails */}
          <div className="grow flex flex-row gap-4 max-h-full overflow-hidden">
            <div className="flex flex-col gap-4 overflow-y-auto min-w-[10vw] max-w-[10vw]">
              {allScenes?.map((scene) => (
                <Card
                  size="small"
                  key={scene.assetId}
                  onClick={() => handleSceneClick(scene.key)}
                  imgFit="contain"
                  imageUrl={`${ENV.API_PLATFORM_URL}/pipeline/renderer/images/v1?prefix=${projectId}/${studyId}&key=${scene.key}&maxWidth=100`}
                  className={`cursor-pointer hover:opacity-100 ${scene.key === sceneKey ? "opacity-100 shadow-lg" : "opacity-60"}`}
                >
                  <p className="font-bold p-4 truncate">{scene.assetName}</p>
                </Card>
              ))}
            </div>

            {/* Image Viewer */}
            <SceneRender projectId={projectId} studyId={studyId} sceneKey={currentScene.key} />

            {/* Insight Panel */}
            <SceneMetricsDetails currentScene={currentScene} studyId={studyId} projectId={projectId} />

            {/* Copilot */}
            {isSceneCopilotEnabled && showCopilot && (
              <Copilot
                projectId={projectId}
                studyId={studyId}
                assetId={sceneKey}
                reportType="attention"
                metric="visibility"
                studyType="scene"
                onClose={() => setShowCopilot(false)}
              />
            )}
          </div>
        </>
      )}
    </ReportsLayout>
  );
};
